/* eslint-disable react/no-multi-comp */
import React, { useEffect, useState } from 'react';
import { useLocation, matchPath } from 'react-router';
import { useSelector, useStore } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, styled } from '@material-ui/styles';
import {
  Drawer,
  Divider,
  List,
  ListSubheader,
  Hidden,
  colors,
  IconButton,
  Box
} from '@material-ui/core';
import MenuOpen from '@material-ui/icons/MenuOpen';
import Menu from '@material-ui/icons/Menu';
import NavItem from 'src/components/NavItem';
import navConfig from './navConfig';
import integratedCompanyNavConfig from './integratedCompanyNavConfig';
import superAdminNavConfig from './superAdminNavConfig';
import { colorPalette } from '../../theme/palette';
import { userRoles } from '../../reducers/sessionReducer';
import { canSeeItem } from 'src/utils/roleUtils';
import { selectCompany } from 'src/actions/company/companySelectors';
import i18next from 'i18next';


const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256
  },
  navigation: {
    overflow: 'auto',
    padding: theme.spacing(0, 2, 2, 2),
    flexGrow: 1,
    backgroundImage: `linear-gradient(197deg, ${colorPalette[2]}, ${colorPalette[1]})`
  },
  profile: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center'
  },
  badge: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
  },
  badgeDot: {
    height: 9,
    minWidth: 9
  },
  onlineBadge: {
    backgroundColor: colors.green[600]
  },
  awayBadge: {
    backgroundColor: colors.orange[600]
  },
  busyBadge: {
    backgroundColor: colors.red[600]
  },
  offlineBadge: {
    backgroundColor: colors.grey[300]
  },
  avatar: {
    cursor: 'pointer',
    width: 40,
    height: 40
  },
  details: {
    marginLeft: theme.spacing(2)
  },
  moreButton: {
    marginLeft: 'auto',
    color: colors.blueGrey[200]
  },
  subheader: {
    color: 'white',
    fontSize: '12px',
    textTransform: 'uppercase',
    fontWeight: 'bold'
  },
  logo: {
    height: '80px',
    margin: '20px 0'
  },
  companyName: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    textAlign: 'center',
    fontFamily: theme.typography.fontFamily,
    //whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  companyNameDivider: {
    borderTop: 'solid 1px',
    marginTop: '8px',
    borderBottom: 'none',
    height: '1px',
    color: colors.blueGrey[200],
    marginLeft: '-16px',
    width: '120%',
  }
}));

const DrawerHeader = styled('span')(({ theme }) => ({
  display: 'flex',
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end'
}));

function renderNavItems({
  // eslint-disable-next-line react/prop-types
  items,
  subheader,
  key,
  classes,
  neededActions,
  ...rest
}) {
  if (!canSeeItem(rest.superAdminActions, neededActions)) {
    return;
  }
  return (
    <List key={key}>
      {subheader && (
        <ListSubheader className={classes.subheader} disableSticky>
          {subheader}
        </ListSubheader>
      )}
      {/* eslint-disable-next-line react/prop-types */}
      {items.reduce(
        // eslint-disable-next-line no-use-before-define
        (acc, item) =>
          reduceChildRoutes({
            acc,
            item,
            superAdminActions: rest.neededActions,
            ...rest
          }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  isSuper,
  adminRole,
  superAdminActions,
  depth = 0
}) {
  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });
    if (canSeeItem(superAdminActions, item.neededActions)) {
      acc.push(
        <NavItem
          depth={depth}
          icon={item.icon}
          key={item.href}
          label={item.label}
          open={Boolean(open)}
          title={item.title}
        >
          {renderNavItems({
            depth: depth + 1,
            pathname,
            items: item.items
          })}
        </NavItem>
      );
    }
  } else {
    if (item.isAdminFull && adminRole !== 'full') {
      return acc;
    }

    if (item.isSuperAdminOnly && !isSuper) {
      //
      return acc;
    }

    if (canSeeItem(superAdminActions, item.neededActions)) {
      acc.push(
        <NavItem
          depth={depth}
          href={item.href}
          icon={item.icon}
          key={item.href}
          label={item.label}
          title={item.translate ? i18next.t(item.title) : item.title}
        />
      );
    }
  }

  return acc;
}

function filter2FASuperAdminNavConfig(twoFactorAuthEnabled, enforced2FA, navConfig) {
  if (!twoFactorAuthEnabled && enforced2FA) {
    return navConfig.filter(item => !item.requires2FA);
  }
  return navConfig;
}

function NavBar({ openDrawer, toggleDrawer, drawerWidth, className, ...rest }) {
  const classes = useStyles();
  const location = useLocation();
  const store = useStore();
  const userRole = store.getState().session.user.role;
  const isSuper = !!store.getState().session.user.superToken;
  const twoFactorAuthEnabled = store.getState().session.user.twoFactorAuthEnabled;
  const enforced2FA = store.getState().session.user.enforced2FA;
  const adminRole = store.getState().session.user.adminRole;
  const superAdminActions = store.getState().session.user.superAdminActions;
  const currentCompany = useSelector(selectCompany);
  const [companyName, setCompanyName] = useState("");
  
  useEffect(() => {
    if(currentCompany?.name){
      setCompanyName(currentCompany.name);
      const companyCountry  = currentCompany?.countryCode;
      i18next.changeLanguage(companyCountry);
    }
  }, [currentCompany]);

  const isSuperAdmin = userRole === userRoles.SUPERADMIN;
  let useNavConfig =
    isSuperAdmin
      ? filter2FASuperAdminNavConfig(twoFactorAuthEnabled, enforced2FA, superAdminNavConfig)
      : navConfig;
  const isCompanyName = companyName?.length > 0;
  let isIntegrationCompany = !!store.getState().company.currentCompany?.integrationType;
  if (isIntegrationCompany && !isSuperAdmin) {
    useNavConfig = integratedCompanyNavConfig
  }
  const handleDrawerClose = () => {
    toggleDrawer(false);
  };


  useEffect(() => {
    if (openDrawer && toggleDrawer) {
      handleDrawerClose();
    }

    // eslint-disable-next-line
  }, [location.pathname]);

  const content = (
    <div {...rest} className={clsx(classes.root, className)}>
      <nav className={classes.navigation}>
        <img
          className={classes.logo}
          src="/images/logo_nav.svg"
          alt="Buk Adelantos"
        />
        <div style={{ display: isCompanyName ? "block" : "none" }}>
          <p className={classes.companyName}>{companyName} </p>
          <hr className={classes.companyNameDivider}></hr>
        </div>

        {useNavConfig.map(list => {
          return renderNavItems({
            items: list.items,
            subheader: list.subheader,
            pathname: location.pathname,
            key: list.subheader,
            neededActions: list.neededActions,
            isSuper,
            adminRole,
            superAdminActions,
            classes
          });
        })}
      </nav>
      <Divider className={classes.divider} />
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      <Hidden mdUp>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {openDrawer ? <MenuOpen /> : <Menu />}
          </IconButton>
        </DrawerHeader>
        <Drawer
          anchor="left"
          classes={{
            paper: classes.mobileDrawer
          }}
          onClose={handleDrawerClose}
          open={openDrawer}
          ModalProps={{
            keepMounted: true
          }}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden smDown>
        <Drawer
          classes={{
            paper: classes.desktopDrawer
          }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </Box>
  );
}

NavBar.propTypes = {
  className: PropTypes.string,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
